"use client";

import { useState, useEffect } from "react";
import { Menu } from "@chakra-ui/react";
import { Avatar } from "@chakra-ui/react";
import { isAuthenticated } from "../../../middlewares/auth";
import { useAuth } from "../../../hooks/auth";
import { useRouter } from "next/navigation";
import {
  Box,
  Flex,
  Text,
  Button
} from "@chakra-ui/react";
// Icons
import { Bell, LogOut } from 'lucide-react';
// Components
import { getUserFirstName } from "utils/userUtils";
import { CustomDialog } from "components/ui/dialog";
// Styles 
import { HeaderComponentsStyles } from "styles/headerStyles";
import { usePermission } from "hooks/userPermissionHooks";
import { useUnreadNotifications } from "hooks/useUnreadNotifications";

const CounterBadge = ({ count }: { count: number }) => {
  if (count <= 0) return null;

  return (
    <Flex
      align="center"
      justify="center"
      minW="20px"
      h="20px"
      px="1.5"
      bg="red.600"
      color="white"
      borderRadius="full"
      fontSize="xs"
      fontWeight="bold"
      lineHeight="1"
    >
      {count > 99 ? "99+" : count}
    </Flex>
  );
};

export const DesktopUserMenu = () => {
  const [userIsAuthenticated, setUserIsAuthenticated] = useState<boolean | null>(null);
  const router = useRouter();
  const { logout } = useAuth();
  const [userName, setUserName] = useState<string>("");
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { unreadCount } = useUnreadNotifications();

  useEffect(() => {
    const fetchName = async () => {
      const name = localStorage.getItem("firstName");
      if (name) {
        setUserName(name);
      } else {
        try {
          const nameFetched = await getUserFirstName();
          if (nameFetched) {
            setUserName(nameFetched);
          }
        } catch (error) {
          console.error("Erro ao buscar nome do usuário:", error);
        }
      }
    };

    const checkAuthentication = async () => {
      setIsLoading(true);
      const isAuthenticatedClient = isAuthenticated();
      setUserIsAuthenticated(isAuthenticatedClient);

      if (isAuthenticatedClient) {
        await fetchName();
      }
      setIsLoading(false);
    };

    checkAuthentication();
  }, []);

  const handleMenuItemClick = (value: string) => {
    setIsLoading(true);

    setTimeout(() => {
      window.location.href = value; 
    }, 500);
  };



  const handleLogout = async () => {
    setIsLoading(true);
    try {
      await logout();
      setIsOpen(false);
      window.location.href = "/";
    } catch (error) {
      console.error("Erro ao fazer logout:", error);
      setIsLoading(false);
    }
  };

  const handleLoginClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setTimeout(() => {
      window.location.href = "/conta/login";
    }, 500);
  };

  const handleSignupClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setTimeout(() => {
      window.location.href = "/conta/cadastro";
    }, 500);
  };


  const handleClassifiedItemClick = (url: string) => {
    setIsLoading(true);
    window.location.href = url;
  };

  const { hasPermission } = usePermission();

  return (
    <>
      {isLoading}
      <div suppressHydrationWarning>
        {userIsAuthenticated ? (
          <Box display="flex" flexDirection="row" alignItems="center" gap={4}>
            <Menu.Root
              open={isMenuOpen}
              onOpenChange={(details) => setIsMenuOpen(details.open)}
            >
              <Menu.Trigger _focusVisible={{ outline: "none", boxShadow: "none" }}>
                <Flex
                  alignItems="center"
                  cursor="pointer"
                  position="relative"
                  transition="transform 0.2s ease-in-out"
                  _hover={{ transform: "scale(1.05)" }}
                  onMouseEnter={() => setIsMenuOpen(true)}
                  onMouseLeave={() => setIsMenuOpen(false)}
                >
                  <Avatar.Root size="xl" boxShadow="sm" _selected={{border:"none"}} _hover={{ transform: "scale(1.08)",bg:"red.700",border:"none",boxShadow:"lg" }}>
                    <Avatar.Fallback />
                  </Avatar.Root>
                  {unreadCount > 0 && (
                    <Box position="absolute" top="-1" right="-1">
                      <CounterBadge count={unreadCount} />
                    </Box>
                  )}
                </Flex>
              </Menu.Trigger>
              <Menu.Positioner>
                <Menu.Content
                  {...HeaderComponentsStyles.userMenuDesktop.menuContent}
                  onMouseEnter={() => setIsMenuOpen(true)}
                  onMouseLeave={() => setIsMenuOpen(false)}
                >
                  <Box px="3" py="2">
                    <Menu.Item
                      value="/conta/menu/notificacoes"
                      onClick={() => handleMenuItemClick("/conta/menu/notificacoes")}
                      {...HeaderComponentsStyles.userMenuDesktop.menuItem}
                    >
                      <Flex align="center" justify="space-between" gap="4" w="100%" minW="170px">
                        <Flex align="center" gap="2" minW="0">
                          <Bell size={15} />
                          <Text>Notificações</Text>
                        </Flex>
                        <CounterBadge count={unreadCount} />
                      </Flex>
                    </Menu.Item>
                  </Box>

                  {/* --- Categoria: Conta --- */}
                  <Box px="3" py="2">
                    <Text fontSize="sm" fontWeight="bold" color="gray.500" mb="1">Minha conta</Text>
                    <Menu.Item
                      value="/conta/menu/minha-conta"
                      onClick={() => handleMenuItemClick("/conta/menu/minha-conta")}
                      {...HeaderComponentsStyles.userMenuDesktop.menuItem}
                    >
                      Detalhes da Conta
                    </Menu.Item>
                  {hasPermission('peca', 1) && (
                    <Menu.Item
                      value="/conta/menu/banco-de-pecas"
                      onClick={() => handleMenuItemClick("/conta/menu/banco-de-pecas")}
                      {...HeaderComponentsStyles.userMenuDesktop.menuItem}
                    >
                      Banco de peças
                    </Menu.Item>
                  )}
                  </Box>

                  {/* --- Categoria: Documentos --- */}
                  {hasPermission('documento', 1) && (
                  <Box px="3" py="2">
                    <Text fontSize="sm" fontWeight="bold" color="gray.500" mb="1">Documentos</Text>
                    <Menu.Item
                      value="/conta/menu/documentos-internos"
                      onClick={() => handleMenuItemClick("/conta/menu/documentos-internos")}
                      {...HeaderComponentsStyles.userMenuDesktop.menuItem}
                    >
                      Internos
                    </Menu.Item>
                    <Menu.Item
                      value="/conta/menu/documentos-internos?categoria=requerimentos"
                      onClick={() => handleMenuItemClick("/conta/menu/documentos-internos?categoria=requerimentos")}
                      {...HeaderComponentsStyles.userMenuDesktop.menuItem}
                    >
                      Pleitos AAJUMP
                    </Menu.Item>
                  </Box>
                  )}

                  {/* --- Categoria: Permutas --- */}
                  {hasPermission('permuta', 1) && (
                  <Box px="3" py="2">
                    <Text
                      fontSize="sm"
                      fontWeight="bold"
                      mb="1"
                      onClick={() => handleMenuItemClick("/conta/menu/banco-de-permutas")}
                      {...HeaderComponentsStyles.userMenuDesktop.menuItemParent}
                    >
                        Banco de permutas
                    </Text>
                  </Box>
                  )}
                  
                  {/* --- Categoria: Classificados --- */}
                  <Box px="3" py="2">
                    <Text fontSize="sm" fontWeight="bold" color="gray.500" mb="1">Classificados</Text>
                    <Menu.Item
                      value="/classificados?view=meus-classificados"
                      onClick={() => handleClassifiedItemClick("/classificados?view=meus-classificados")}
                      {...HeaderComponentsStyles.userMenuDesktop.menuItem}
                    >
                      Meus classificados
                    </Menu.Item>
                  </Box>
                </Menu.Content>
              </Menu.Positioner>
            </Menu.Root>
            <Box ml="2" color="black" textAlign="center">
              <Text>{userName}</Text>
              <Flex
                {...HeaderComponentsStyles.userMenuDesktop.logoutButton}
                onClick={() => setIsOpen(true)}
                cursor="pointer"
              >
                <LogOut size="15" />
                <Text>Sair</Text>
              </Flex>
              <CustomDialog
                open={isOpen}
                onClose={({ open }) => setIsOpen(open)}
                title="Sair da conta"
                body="Você tem certeza que deseja sair?"
                onCancel={() => setIsOpen(false)}
                onConfirm={handleLogout}
                confirmText="Sair"
                cancelText="Cancelar"
              />
            </Box>
          </Box>
        ) : (
          <Box {...HeaderComponentsStyles.userMenuDesktop.desktopUserButtonsBox}>
            <Button 
              {...HeaderComponentsStyles.userMenuDesktop.desktopLoginButton}
              onClick={handleLoginClick}
            >
              Entrar
            </Button>
            <Button 
              {...HeaderComponentsStyles.userMenuDesktop.desktopSignupButton}
              onClick={handleSignupClick}
            >
              Associe-se
            </Button>
          </Box>
        )}
      </div>
    </>
  );
};
